import React, { useEffect, useMemo, useState } from "react";
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import { useSelector } from "react-redux";
import api, { apiError } from "../../lib/api";
import RenderToast from "../../appcomponents/RenderToast";
import TableComp from "../../appcomponents/TableComp";
import PaginationComp from "../../appcomponents/PaginationComp";
import ConfirmModal from "../../Modal/ConfirmModal";
import useDebounce from "../../hooks/useDebounce";
import { hasPermission, selectUser } from "../../store/authSlice";
import styles from "./Role.module.css";

const PERMISSIONS = [
  { page: "dashboard", label: "Dashboard", actions: ["view"] },
  { page: "blogs", label: "Blogs", actions: ["view", "add", "edit", "delete", "delete_all"] },
  { page: "users", label: "Users", actions: ["view", "add", "edit", "delete"] },
  { page: "profile", label: "Profile", actions: ["view", "edit"] },
  { page: "category", label: "Category", actions: ["view", "add", "edit", "delete"] },
  { page: "tags", label: "Tags", actions: ["view", "add", "edit", "delete"] },
  { page: "status", label: "Status", actions: ["view", "add", "edit", "delete"] },
  { page: "role", label: "Role", actions: ["view", "add", "edit", "delete"] },
];

const actionLabel = (action) => {
  if (action === "delete_all") return "Delete All";
  return action.charAt(0).toUpperCase() + action.slice(1);
};

const emptyPermissions = () => {
  const perms = {};
  PERMISSIONS.forEach((section) => {
    perms[section.page] = {};
    section.actions.forEach((action) => {
      perms[section.page][action] = false;
    });
  });
  return perms;
};

export default function Role() {
  const currentUser = useSelector(selectUser);
  const [roles, setRoles] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState(emptyPermissions());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewRole, setViewRole] = useState(null);
  const debouncedSearch = useDebounce(search, 350);
  const canAdd = hasPermission(currentUser, "role", "add");
  const canView = hasPermission(currentUser, "role", "view");
  const canEdit = hasPermission(currentUser, "role", "edit");
  const canDelete = hasPermission(currentUser, "role", "delete");

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/roles.php", { params: { search: debouncedSearch, page, per_page: 10 } });
      const payload = response.data?.data || {};
      setRoles(payload.items || []);
      setTotal(payload.total || 0);
      setSelectedIds([]);
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, page]);

  useEffect(() => {
    if (!modalOpen) {
      setEditingRole(null);
      setName((prev) => (prev === "" ? prev : ""));
      setPermissions((prev) => {
        const next = emptyPermissions();
        return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      });
      return;
    }
    if (editingRole) {
      const next = emptyPermissions();
      PERMISSIONS.forEach((section) => {
        section.actions.forEach((action) => {
          next[section.page][action] = Boolean(editingRole.permissions?.[section.page]?.[action]);
        });
      });
      setName((prev) => (prev === (editingRole.name || "") ? prev : (editingRole.name || "")));
      setPermissions((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    }
  }, [modalOpen, editingRole?.id]);

  const permissionCount = useMemo(
    () =>
      Object.values(permissions).reduce(
        (sum, section) => sum + Object.values(section).filter(Boolean).length,
        0
      ),
    [permissions]
  );

  const openCreate = () => {
    if (!canAdd) return;
    setEditingRole(null);
    setModalOpen(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      RenderToast({ type: "error", message: "Role name is required." });
      return;
    }

    setSaving(true);
    try {
      const response = await api.post("/api/roles.php", {
        action: editingRole ? "update" : "add",
        ...(editingRole?.id ? { id: editingRole.id } : {}),
        name,
        permissions,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to save role.");
      }
      RenderToast({ type: "success", message: editingRole ? "Role updated successfully." : "Role created successfully." });
      setModalOpen(false);
      fetchData();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const response = await api.post("/api/roles.php", { action: "delete", id: deleteTarget.id });
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to delete role.");
      }
      RenderToast({ type: "success", message: "Role deleted successfully." });
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setDeleteLoading(true);
    try {
      await Promise.all(selectedIds.map((id) => api.post("/api/roles.php", { action: "delete", id })));
      RenderToast({ type: "success", message: "Selected roles deleted successfully." });
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setDeleteLoading(false);
    }
  };

  const allSelected = roles.length > 0 && selectedIds.length === roles.length;

  const columns = [
    {
      key: "select",
      label: <input type="checkbox" checked={allSelected} onChange={() => setSelectedIds(allSelected ? [] : roles.map((role) => role.id))} />,
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.id)}
          onChange={() =>
            setSelectedIds((prev) =>
              prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id]
            )
          }
        />
      ),
    },
    { key: "name", label: "Role Name" },
    {
      key: "permission_count",
      label: "Permission Count",
      render: (row) => `${Object.values(row.permissions || {}).flatMap((section) => Object.values(section)).filter(Boolean).length} assigned`,
    },
    {
      key: "action",
      label: "Action",
      render: (row) => (
        <div className={styles.actions}>
          {canView ? (
            <button type="button" onClick={() => setViewRole(row)} title="View">
              <FiEye />
            </button>
          ) : null}
          {canEdit ? (
            <button type="button" onClick={() => openEdit(row)} title="Edit">
              <FiEdit2 />
            </button>
          ) : null}
          {canDelete ? (
            <button type="button" onClick={() => setDeleteTarget(row)} title="Delete">
              <FiTrash2 />
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1>Role</h1>
          <p>Assign which pages and actions each role can access.</p>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch />
          <input
            type="search"
            value={search}
            placeholder="Search roles..."
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
        </div>
        <div className={styles.toolbarActions}>
          {selectedIds.length > 0 && canDelete && (
            <button type="button" className={styles.deleteBtn} onClick={handleBulkDelete} disabled={deleteLoading}>
              <FiTrash2 />
              Delete Selected
            </button>
          )}
          {canAdd ? (
            <button type="button" className={styles.addBtn} onClick={openCreate}>
              <FiPlus />
              Add Role
            </button>
          ) : null}
        </div>
      </div>

      <TableComp columns={columns} rows={roles} loading={loading} emptyMessage="No roles found." />

      <div className={styles.paginationRow}>
        <PaginationComp total={total} perPage={10} currentPage={page} onPageChange={setPage} />
      </div>

      {modalOpen && (
        <div className={styles.modalOverlay} onClick={() => setModalOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <h3>{editingRole ? "Edit Role" : "Add Role"}</h3>
              <p>{permissionCount} permissions selected</p>
            </div>
            <form className={styles.form} onSubmit={handleSubmit}>
              <label className={styles.fullWidth}>
                <span>Role Name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} />
              </label>

              <div className={styles.fullWidth}>
                {PERMISSIONS.map((section) => (
                  <div key={section.page} className={styles.permissionCard}>
                    <div className={styles.permissionTitle}>{section.label}</div>
                    <div className={styles.permissionGrid}>
                      {section.actions.map((action) => (
                        <label key={action} className={styles.checkItem}>
                          <input
                            type="checkbox"
                            checked={Boolean(permissions[section.page]?.[action])}
                            onChange={() =>
                              setPermissions((prev) => ({
                                ...prev,
                                [section.page]: {
                                  ...prev[section.page],
                                  [action]: !prev[section.page]?.[action],
                                },
                              }))
                            }
                          />
                          <span>{actionLabel(action)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.modalActions}>
                <button type="button" className={styles.secondaryBtn} onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={saving}>
                  {saving ? "Saving..." : editingRole ? "Update Role" : "Add Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewRole && (
        <div className={styles.modalOverlay} onClick={() => setViewRole(null)}>
          <div className={styles.viewModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <h3>Role Details</h3>
            </div>
            <div className={styles.viewBody}>
              <div className={styles.viewRow}><span>Name</span><strong>{viewRole.name}</strong></div>
              <div className={styles.viewRow}><span>Permissions</span><strong>{Object.values(viewRole.permissions || {}).flatMap((section) => Object.values(section)).filter(Boolean).length}</strong></div>
            </div>
            <div className={styles.modalActions}>
              <button type="button" className={styles.secondaryBtn} onClick={() => setViewRole(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        title="Delete Role"
        message={`Are you sure you want to delete ${deleteTarget?.name || "this role"}?`}
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
